import { ChartDataset } from "chart.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./Table";
import { ForwardedChartHandle } from "../shared/Chart/chart";
import { useHighlightDataset, useToggleDatasets } from "./Chart/hooks";
import classNames from "classnames";
import { LegendIndicator } from "./LegendIndicator";

interface Props {
  chartRef: React.RefObject<ForwardedChartHandle>;
  datasets: ChartDataset[];
  columns?: Record<
    string,
    (dataset: ChartDataset, index: number) => React.ReactNode
  >;
}

export const ChartLegend = ({ chartRef, datasets, columns = {} }: Props) => {
  const { isDatasetVisible, visibilityHandlers } = useToggleDatasets(chartRef);
  const highlightHandlers = useHighlightDataset(chartRef);

  return (
    <Table className="lg:basis-96">
      <TableHead>
        <TableRow>
          <TableHeader>Metric</TableHeader>
          {Object.keys(columns).map((column) => (
            <TableHeader key={column}>{column}</TableHeader>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {datasets.map((dataset, index) => {
          const visible = isDatasetVisible(index);
          const { label, borderColor, data } = dataset;

          return (
            <TableRow
              key={label}
              className={classNames({
                "text-slate-500": !visible,
              })}
              data-visible={visible}
              {...visibilityHandlers(index)}
              {...highlightHandlers(index)}
            >
              <TableCell
                className="font-semibold truncate max-w-0 w-2/5"
                title={label}
              >
                <LegendIndicator
                  color={borderColor as string}
                  className="mr-1.5"
                  selected={visible}
                />
                {label}
              </TableCell>

              {Object.keys(columns).map((column) => (
                <TableCell key={column}>
                  {columns[column](dataset, index)}
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
